/**
 * Normalizes SDK deploy-transfer progress callbacks to a 0–100 percent value.
 *
 * Asset-manager callbacks in this codebase typically use integer 0–100 `percent`
 * fields (same as firmware transfer progress). Fractional 0–1 values are scaled
 * only when strictly between 0 and 1 so that `1` is treated as 1%, not 100%.
 */
export function normalizeProgramDeployProgressPercent(progress: unknown): number | null {
  if (progress == null) {
    return null;
  }

  if (typeof progress === 'number' && Number.isFinite(progress)) {
    return clampPercent(scaleTransferPercentField(progress));
  }

  if (typeof progress !== 'object') {
    return null;
  }

  const record = progress as Record<string, unknown>;

  // Terminal asset-updater state after completion; must not reset the bar to 0%.
  if (typeof record.state === 'string' && record.state === 'idle') {
    return null;
  }

  if (typeof record.percent === 'number' && Number.isFinite(record.percent)) {
    return clampPercent(scaleTransferPercentField(record.percent));
  }

  if (typeof record.progress === 'number' && Number.isFinite(record.progress)) {
    return clampPercent(scaleTransferPercentField(record.progress));
  }

  if (
    typeof record.loaded === 'number' &&
    Number.isFinite(record.loaded) &&
    typeof record.total === 'number' &&
    Number.isFinite(record.total) &&
    record.total > 0
  ) {
    return clampPercent((record.loaded / record.total) * 100);
  }

  return null;
}

function scaleTransferPercentField(value: number): number {
  if (value > 0 && value < 1) {
    return value * 100;
  }

  return value;
}

export function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, value));
}
